import React from "react";
import { PieChart, Pie, Cell, Legend } from "recharts";
import { SIGNOL_COLOURS } from "@signolteam/shared/colours";
import { useDCContext, useAuthContext } from "@src/context";
import { useManagerContext } from "@src/context/managerContext";
import ManagerHooks from "@src/context/hooks/managerHooks";
import { AppUtils, SignolUtils } from "@src/utils";
import { Divider, DropDown, Loader, Tooltip } from "@src/components/atoms";
import PieChartLib from "@src/components/graphs/PieChart";
import {
  BehaviourSuccessOrSavingsPerOperator,
  ManagersBehaviourChangeCard,
} from "@src/components/cards/manager";
import MonthlyDateDropDown from "@src/components/atoms/manager/MonthlyDateDropDown";
import PerformanceDataCountsAndPieChartCard from "./PerformanceDataCountsAndPieChartCard";
import PerformanceFuelSavingsVsPreCard from "./PerformanceFuelSavingsVsPreCard";
import PerformanceSuccessRateVsPreCard from "./PerformanceSuccessRateVsPreCard";
import TeamPicker from "./TeamPicker";
import PerformanceSuccessRateSection from "./PerformanceSuccessRateSection";
import PerformanceFuelSavingsSection from "./PerformanceFuelSavingsSection";

const PerformanceTab = () => {
  const { profile, selectedPeriod, selectedBehaviour, selectedTeam } =
    useManagerContext();
  const { getDynamicReplacement } = useDCContext();
  const auth = useAuthContext();

  const teams = profile.teams || [];
  const selectedBehaviourAcronym = selectedBehaviour.behaviourAcronym;
  const isMaritime = profile.customer.industry === "shipping";
  const shouldShowTeamFilter =
    teams.length > 1 && profile.customer.customerCode !== "CHR";
  const uiShowFuelSavings = !isMaritime;

  //Check if we have enough data to display the Performance tab (Success rate change data must exist)
  const {
    data: behaviourSuccessVsPreSignol = [],
    isPending: isPendingBehaviourSuccessVsPre,
    error: errorBehaviourSuccessVsPre,
  } = ManagerHooks.useGetBehaviourSuccessVsPreSignol(
    auth.activeRole,
    selectedTeam?.id,
    selectedPeriod,
    AppUtils.endOfMonthIfValidDate(selectedPeriod),
  );
  const currentBvrSuccessVsPre = behaviourSuccessVsPreSignol?.find(
    (d) => d.behaviourAcronym === selectedBehaviourAcronym,
  );
  const hasPostSignolData =
    currentBvrSuccessVsPre?.avgSuccessRatePostSignolWeighted?.value != null;

  /** Check selected team's targetBehaviours includes selectedBehaviour */
  const teamNotNudged =
    selectedTeam != null &&
    !selectedTeam.targetBehaviours.some(
      (b) => b.acronym === selectedBehaviourAcronym,
    );

  const state = isPendingBehaviourSuccessVsPre
    ? "loading"
    : teamNotNudged
      ? "invalid-notNudged"
      : !hasPostSignolData
        ? "invalid-missing"
        : "valid";

  return (
    <div className="h-full">
      {shouldShowTeamFilter && (
        <section className="bg-primary-500 text-black">
          <TeamPicker />
        </section>
      )}

      <section className="p-6 md:w-1/2 lg:w-1/4">
        <div className="flex flex-row items-center gap-2">
          <MonthlyDateDropDown />
          <Tooltip
            content={[
              getDynamicReplacement(
                "fuel_manager__tooltip__behaviour_performance_alltime",
              ),
            ]}
            direction={SignolUtils.isMobile() ? "down-left" : "down-right"}
            absolute={false}
          />
        </div>
      </section>

      {state === "loading" && <Loader size="2xl" />}

      {state === "invalid-notNudged" && (
        <div className="w-full h-full flex justify-center items-center px-12">
          {getDynamicReplacement(
            "fuel_manager__behaviour__team_filter_team_gets_no_feedback",
          )}
        </div>
      )}
      {state === "invalid-missing" && (
        <div className="w-full h-full flex justify-center items-center px-12">
          {getDynamicReplacement(
            "fuel_manager__behaviour__missing_pre_or_post_signol_data",
            {},
            "We currently don't have data for this month. If you'd like to discuss this further, please contact your Signol account manager.",
          )}
        </div>
      )}

      {state === "valid" && (
        <>
          {/* top cards */}
          <section>
            <div className="px-8 flex flex-wrap items-start justify-center gap-4 p-6">
              {uiShowFuelSavings && <PerformanceFuelSavingsVsPreCard />}
              {!errorBehaviourSuccessVsPre && (
                <PerformanceSuccessRateVsPreCard
                  isPendingBehaviourSuccessVsPre={
                    isPendingBehaviourSuccessVsPre
                  }
                  currentBvrSuccessVsPre={currentBvrSuccessVsPre}
                />
              )}
              <PerformanceDataCountsAndPieChartCard />
            </div>
          </section>

          <Divider padding={true} />
          <PerformanceSuccessRateSection />

          {uiShowFuelSavings && (
            <>
              <Divider padding={true} />
              <PerformanceFuelSavingsSection />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default PerformanceTab;
